import { useCallback, useEffect, useState } from 'react';

type WebkitFullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
};

function hasFullscreenElement() {
  const documentWithWebkit = document as WebkitFullscreenDocument;
  return Boolean(document.fullscreenElement || documentWithWebkit.webkitFullscreenElement);
}

/** Tracks browser fullscreen so the same control can enter and exit it. */
export function usePlayerFullscreen(
  present: () => void,
  dismiss: () => void,
) {
  const [fullscreenActive, setFullscreenActive] = useState(false);

  useEffect(() => {
    const syncFullscreen = () => setFullscreenActive(hasFullscreenElement());
    syncFullscreen();
    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncFullscreen);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (hasFullscreenElement()) dismiss();
    else present();
  }, [dismiss, present]);

  return { fullscreenActive, toggleFullscreen };
}
